// Global CRM constants
export const NO_SPECIAL_CHARS_REGEX = /^[a-zA-Z0-9\n @!$*"',:%.^#?/()_&+-]*$/;
export const FULL_NAME_REGEX = /^[A-Za-z ]+$/;
export const UEN_NUMBER_REGEX = /^[A-Z0-9]+$/;
export const CONTACT_NUMBER_REGEX = /^\d+$/;
export const EMAIL_REGEX = /^\w+([.-]?\w+)*@\w+([.-]?\w+)*(\.\w{2,3})+$/;

// ClarificationForm constants
export const CLARIFICATION_INPUT_ERROR = {
  comment: {
    required: 'Required Field',
    specialChars:
      'Only these special characters (@!$*"\',:%.^#?/()_&+-) are allowed.',
  },
};

// FeedbackForm constants
export const INITIAL_FORM_VALUE = {
  topic: '',
  salutation: '',
  fullName: '',
  role: '',
  entityType: '',
  uenNumber: '',
  contactNumberType: 'Office Number',
  contactNumber: '',
  email: '',
  question: '',
  // Input that will appear based on certain conditions
  transactionNumber: '',
  nameApplicationNumber: '',
  arn: '',
  refundApplicationNumber: '',
  depositAccountNumber: '',
  fillingAgentNumber: '',
  dateOfPurchase: '',
};

// Adding this to resolve Sonar issue of duplicate code block - assuming that the fields of Label and Value for this dropdown will always be identical.
const OPTIONS_LIST = [
  'About ACRA',
  'BizFile Matters',
  'Business Matters',
  'Company Matters',
  'Compliance Matters',
  'Approved Refund/Payment Status',
  'Financial Reporting Matters (other than XBRL)',
  'General Enquiries (other than XBRL & UEN)',
  'Limited Liability Partnership Matters',
  'Limited Partnership Matters',
  'Online Training Matters',
  'Policies and Legislation',
  'Public Accountants',
  'API Subscription / Purchase of Information (iShop)',
  'Service Quality Matters',
  'Unique Entity Number (UEN)',
  'VCC Matters',
  'XBRL',
];

export const TOPIC_OPTIONS = OPTIONS_LIST.map((option) => ({
  label: option,
  value: option,
}));

export const TOPIC_INFO: Record<string, string> = {
  'BizFile Matters':
    'For enquiries on technical information on BizFile transactions such as system problems encountered when navigating BizFile or difficulties faced when lodging BizFile transactions.',
  'Business Matters':
    'For enquiries on business firm registration, changes to business particulars, business renewal and other related business matters. Your business entity is classified as business firm (sole proprietors or partnerships) if the UEN (previously known as registration number) is made up of 8 digits with 1 alphabet, e.g. 12345678C.',
  'Company Matters':
    'For enquiries on company incorporation, changes to company particulars, filing of Annual Returns and other related company matters.Your business entity is classified as a company if the name ends with "Pte Ltd" or "Private Limited" or "ltd" or "Limited" and the UEN (previously known as registration number) is made up of 9 digits with 1 alphabet, e.g. 199912345C.',
  'Limited Partnership Matters':
    'For enquiries on Limited Partnerhship (LP) registration and other LP related matters. Your business entity is classified as LP if the registration number starts with "T" followed by 8 digits and 1 alphabet, eg T09LP0079D.',
  'Unique Entity Number (UEN)':
    'For enquires on Unique Entity Number (UEN) such as the types of ACRA entities that will be affected by UEN and how businesses, local companies, foreign companies, limited liability partnerships and public accounting firms can make a smooth transition to UEN.',
};

export const SALUTATION_OPTIONS = [
  {
    label: 'Mr.',
    value: 'Mr.',
  },
  {
    label: 'Mrs.',
    value: 'Mrs.',
  },
  {
    label: 'Ms.',
    value: 'Ms.',
  },
  {
    label: 'Mdm.',
    value: 'Mdm.',
  },
  {
    label: 'Dr.',
    value: 'Dr.',
  },
];

export const ROLE_OPTIONS = [
  {
    label: 'Company officer (Director/Secretary)',
    value: 'Company officer (Director/Secretary)',
  },
  {
    label: 'CSP',
    value: 'CSP',
  },
  {
    label: 'Declined Disclosure',
    value: 'Declined Disclosure',
  },
  {
    label: 'General Public',
    value: 'General Public',
  },
  {
    label: 'Others',
    value: 'Others',
  },
  {
    label: 'Owner/Partner/Manager',
    value: 'Owner/Partner/Manager',
  },
  {
    label: 'Representative of Appointed Officer',
    value: 'Representative of Appointed Officer',
  },
  {
    label: 'VCC officer',
    value: 'VCC officer',
  },
];

export const ENTITY_TYPE_OPTIONS = [
  {
    label: 'Foreign Company',
    value: 'Foreign Company',
  },
  {
    label: 'General Public',
    value: 'General Public',
  },
  {
    label: 'Limited Liability Partnership',
    value: 'Limited Liability Partnership',
  },
  {
    label: 'Limited Partnership',
    value: 'Limited Partnership',
  },
  {
    label: 'Local Company',
    value: 'Local Company',
  },
  {
    label: 'Public Accountant',
    value: 'Public Accountant',
  },
  {
    label: 'Sole Proprietorship/Partnership',
    value: 'Sole Proprietorship/Partnership',
  },
  {
    label: 'Variable Capital Companies',
    value: 'Variable Capital Companies',
  },
];

export const CONTACT_NUMBER_TYPE_OPTIONS = [
  {
    id: 'mobile-number-option',
    label: 'Mobile Number',
    value: 'Mobile Number',
  },
  {
    id: 'office-number-option',
    label: 'Office Number',
    value: 'Office Number',
  },
];

export const FEEDBACK_INPUT_ERROR: Record<
  string,
  Record<string, string | Record<string, string>>
> = {
  topic: {
    required: 'Required Field',
  },
  fullName: {
    required: 'Required Field',
    invalid: 'Invalid text! special characters are not allowed',
    salutationRequired: 'Please select the appropriate category',
  },
  role: {
    required: 'Required Field',
  },
  entityType: {
    required: 'Required Field',
  },
  uenNumber: {
    length: 'Please provide a valid UEN with 9 or 10 characters',
    invalid:
      'Enter only uppercase and numerics, and make sure UEN must contain at least one uppercase alphabet',
  },
  contactNumber: {
    required: {
      mobileNumber: 'Required Field',
      officeNumber: 'Required Field',
    },
    minLength: {
      mobileNumber: 'Mobile number must contain minimum of 8 numbers',
      officeNumber: 'Office number must contain minimum of 8 numbers',
    },
    maxLength: {
      mobileNumber: 'Warning! Local number consists only 8 numbers',
      officeNumber: 'Warning! Local number consists only 8 numbers',
    },
  },
  email: {
    required: 'Required Field',
    invalid: 'Please ensure email address is valid',
  },
  question: {
    required: 'Required Field',
    specialChars:
      'Only these special characters (@!$*"\',:%.^#?/()_&+-) are allowed.',
  },
};

// InputForm constants
export const INPUT_INITIAL_FORM_VALUE = {
  name: '',
  email: '',
  comment: '',
};

export const INPUT_FORM_INPUT_ERROR: Record<string, any> = {
  name: {
    required: 'Required Field',
  },
  email: {
    required: 'Required Field',
    invalid: 'Please ensure email address is valid',
  },
  comment: {
    required: 'Required Field',
    specialChars:
      'Only these special characters (@!$*"\',:%.^#?/()_&+-) are allowed.',
  },
};

// SurveryForm constants
export const DUMMY_SURVEY_TEMPLATE = [
  {
    number: '1',
    questions:
      '1. Please rate your overall experience with our Live Chat service..',
  },
  {
    number: '2',
    questions: '2. The Officer was helpful..',
  },
  {
    number: '3',
    questions: '3. The Officer resolved my enquiry..',
  },
  {
    number: '4',
    questions: '4. It was easy for you to get the help you needed today..',
  },
];

export const PLEASE_SELECT_MESSAGE = 'Please select the relevant option';

// SurveyUnsubscription constants
// eslint-disable-next-line import/prefer-default-export
export const EMAIL_ERROR_MESSAGE: Record<string, string> = {
  required: 'Required Field',
  missmatched: 'Email address do not match!',
};
